import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requisicaoCompraService } from '../services/requisicaoCompraService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type {
  CriarRequisicaoCompraRequest,
  AtualizarRequisicaoCompraRequest,
  AprovarRequisicaoCompraRequest,
  StatusRequisicaoCompra,
  FiltrosListaCompras,
} from '../types';

interface UseRequisicaoCompraParams {
  pagina?: number;
  tamanhoPagina?: number;
  status?: StatusRequisicaoCompra;
  /** Busca, período e ordenação (servidor). */
  filtros?: FiltrosListaCompras;
}

export const useRequisicaoCompra = ({ pagina = 1, tamanhoPagina = 20, status, filtros = {} }: UseRequisicaoCompraParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['requisicao-compra', pagina, tamanhoPagina, status, filtros],
    queryFn: () => requisicaoCompraService.getAll(pagina, tamanhoPagina, status, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });

  const requisicoes  = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const porStatus    = paginado?.porStatus;
  const valorTotal   = paginado?.valorTotal;

  const createMutation = useMutation({
    mutationFn: (request: CriarRequisicaoCompraRequest) => requisicaoCompraService.create(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-compra'] });
      toast.success(`Requisição #${data.numero} criada com sucesso`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarRequisicaoCompraRequest }) =>
      requisicaoCompraService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-compra'] });
      toast.success('Requisição atualizada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const enviarMutation = useMutation({
    mutationFn: (id: number) => requisicaoCompraService.enviar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-compra'] });
      toast.success('Requisição enviada para aprovação');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const aprovarMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: AprovarRequisicaoCompraRequest }) =>
      requisicaoCompraService.aprovar(id, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-compra'] });
      toast.success(data.status === 'Aprovada' ? 'Requisição aprovada' : 'Requisição rejeitada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: number) => requisicaoCompraService.cancelar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao-compra'] });
      toast.success('Requisição cancelada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    requisicoes,
    total,
    totalPaginas,
    porStatus,
    valorTotal,
    isLoading,
    isFetching,
    criarRequisicao:    createMutation.mutateAsync,
    atualizarRequisicao: updateMutation.mutateAsync,
    enviarRequisicao:   enviarMutation.mutateAsync,
    aprovarRequisicao:  aprovarMutation.mutateAsync,
    cancelarRequisicao: cancelarMutation.mutateAsync,
    isCreating:  createMutation.isPending,
    isUpdating:  updateMutation.isPending,
    isEnviando:  enviarMutation.isPending,
    isAprovando: aprovarMutation.isPending,
  };
};

export const useRequisicaoCompraDetalhe = (id: number) => {
  return useQuery({
    queryKey: ['requisicao-compra', id],
    queryFn: () => requisicaoCompraService.getById(id),
    enabled: id > 0,
  });
};
